/**
 * After a new version of Davnai goes live, a page that was already open still runs the old version's code. Its
 * buttons then ask the server for actions that no longer exist ("Server Action … was not found"), or for old script
 * files. The fix is simply to load the page again, which picks up the new version.
 */

const KEY = "davnai:stale-reload";
// "An unexpected response was received from the server": a button's request answered by something that isn't this
// version of the app (a deploy going live at that moment). Loading the page again fixes it (2026-10-15, /login).
const STALE = /Server Action .* was not found|Failed to find Server Action|UnrecognizedActionError|ChunkLoadError|Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|An unexpected response was received from the server/i;
// The connection dropped while the page was asking the server for something (Firefox, Chrome, Safari wording).
const OFFLINE = /NetworkError when attempting to fetch resource|^TypeError:? Failed to fetch$|^Failed to fetch$|^Load failed$|The Internet connection appears to be offline|network connection was lost/i;

/** True when the error means this page is running an older version of the app. */
export function isStaleApp(err: unknown): boolean {
  if (!err) return false;
  const e = err as { name?: unknown; message?: unknown };
  return STALE.test(`${String(e.name ?? "")} ${String(e.message ?? err)}`);
}

/** True when the error is only the connection dropping (nothing wrong with the page): shown as "Connection lost". */
export function isConnectionLost(err: unknown): boolean {
  if (!err) return false;
  const e = err as { name?: unknown; message?: unknown };
  const message = String(e.message ?? err).trim();
  return OFFLINE.test(message) || OFFLINE.test(`${String(e.name ?? "")}: ${message}`);
}

/** Loads the page again, at most once a minute (so a real problem can't cause a reload loop). True when it reloads. */
export function reloadForNewVersion(now = Date.now()): boolean {
  if (typeof window === "undefined") return false;
  try {
    const last = Number(window.sessionStorage.getItem(KEY) ?? 0);
    if (now - last < 60_000) return false;
    window.sessionStorage.setItem(KEY, String(now));
  } catch {
    /* storage blocked: still reload once */
  }
  window.location.reload();
  return true;
}
